import React from 'react';
import {
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { BhiDialog } from 'src/UIKit';
import { Button } from 'src/UIKit/components';
import GridData from 'src/components/GridData/GridData';
import TooltipCell from 'src/components/TooltipCell';
import { ShortLink } from '../../types';
import { formatShortUrl } from '../../helpers';
import { getDateTimeUSAFormatWithTime } from 'src/constants/helpers';

interface AnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  shortLink: ShortLink | null;
}

type AnalyticsRow = {
  id: 'desktop' | 'mobile' | 'total';
  deviceType: string;
  last7Days: number;
  last30Days: number;
  allTime: number;
};

const columns: GridColDef<AnalyticsRow>[] = [
  { field: 'deviceType', headerName: 'Device Type', minWidth: 130, flex: 1.3, sortable: false, disableColumnMenu: true },
  { field: 'last7Days', headerName: '7 Days', minWidth: 85, flex: 1, align: 'right', headerAlign: 'right', sortable: false, disableColumnMenu: true },
  { field: 'last30Days', headerName: '30 Days', minWidth: 85, flex: 1, align: 'right', headerAlign: 'right', sortable: false, disableColumnMenu: true },
  { field: 'allTime', headerName: 'All Time', minWidth: 85, flex: 1, align: 'right', headerAlign: 'right', sortable: false, disableColumnMenu: true },
];

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({ open, onClose, shortLink }) => {
  if (!shortLink) return null;

  const analytics = shortLink.analytics;
  const rows: AnalyticsRow[] = analytics ? [
    {
      id: 'desktop',
      deviceType: 'Desktop',
      last7Days: analytics.desktopClicksLast7Days || 0,
      last30Days: analytics.desktopClicksLast30Days || 0,
      allTime: analytics.desktopClicksAllTime || 0,
    },
    {
      id: 'mobile',
      deviceType: 'Mobile',
      last7Days: analytics.mobileClicksLast7Days || 0,
      last30Days: analytics.mobileClicksLast30Days || 0,
      allTime: analytics.mobileClicksAllTime || 0,
    },
    {
      id: 'total',
      deviceType: 'Total',
      last7Days: analytics.totalClicksLast7Days || 0,
      last30Days: analytics.totalClicksLast30Days || 0,
      allTime: analytics.totalClicksAllTime || 0,
    },
  ] : [];

  return (
    <BhiDialog
      open={open}
      onClose={onClose}
      title="Short Link Clicks"
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Stack gap={2}>
          <Stack gap={0.5}>
            <Typography variant="caption" color="text.secondary">Short URL</Typography>
            <TooltipCell value={formatShortUrl(shortLink.code)} variant="body2" />
            <Typography variant="caption" color="text.secondary">Target URL</Typography>
            <TooltipCell value={shortLink.url} variant="body2" />
          </Stack>
          {!!analytics?.lastClickTimestamp && (
            <Typography variant="body2" color="text.secondary">
              Last click: {getDateTimeUSAFormatWithTime(new Date(analytics.lastClickTimestamp))}
            </Typography>
          )}
          {!analytics ? (
            <Stack alignItems="center" py={2}>
              <Typography variant="body2" color="text.secondary">
                No data available.
              </Typography>
            </Stack>
          ) : (
            <GridData
              columns={columns}
              rows={rows}
              pagination={null}
              rowHeight={36}
              columnHeaderHeight={40}
              autoHeight
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </BhiDialog>
  );
};
